import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { OCRExtractedDocument, MultiScanSessionData, MultiOCRResultLine } from '@/types/ocr';

/**
 * Store Zustand pour persister les données OCR extraites
 * Permet de conserver les données même si l'utilisateur change de page ou d'onglet
 *
 * Deux slices :
 * 1. Mono-fichier : extractedData, confidence, editedData, etc.
 * 2. Multi-scan (Prompt I) : multiScanSession avec resultLines sérialisables
 */

const MULTI_SCAN_EXPIRATION_MS = 24 * 60 * 60 * 1000; // 24h

interface OCRStore {
  // === Slice mono-fichier ===
  extractedData: OCRExtractedDocument | null;
  confidence: number;
  editedData: Partial<OCRExtractedDocument>;
  selectedChantiers: Record<string, string>;
  step: 'upload' | 'extraction' | 'review' | 'matching' | 'saving';
  chantierId: string | null;
  documentType: 'facture' | 'devis' | 'ao' | 'frais' | null;
  typeFrais: string | null;

  // Actions mono-fichier
  setExtractedData: (data: OCRExtractedDocument | null, confidence: number) => void;
  setEditedData: (data: Partial<OCRExtractedDocument>) => void;
  updateEditedField: (field: string, value: unknown) => void;
  selectChantier: (ref: string, id: string) => void;
  setStep: (step: OCRStore['step']) => void;
  setContext: (chantierId: string | null, documentType: OCRStore['documentType'], typeFrais?: string) => void;
  clearOCRData: () => void;
  hasData: () => boolean;

  // === Slice multi-scan (Prompt I) ===
  multiScanSession: MultiScanSessionData | null;

  // Actions multi-scan
  setMultiScanSession: (session: MultiScanSessionData) => void;
  updateMultiScanLine: (lineId: string, update: Partial<MultiOCRResultLine>) => void;
  clearMultiScanSession: () => void;
}

const initialState = {
  extractedData: null,
  confidence: 0,
  editedData: {},
  selectedChantiers: {},
  step: 'upload' as const,
  chantierId: null,
  documentType: null,
  typeFrais: null,
  multiScanSession: null,
};

export const useOCRStore = create<OCRStore>()(
  persist(
    (set, get) => ({
      // État initial
      ...initialState,

      // === Actions mono-fichier (inchangées) ===
      setExtractedData: (data, confidence) => set({
        extractedData: data,
        confidence,
        step: data ? 'review' : 'upload',
        editedData: {},
      }),

      setEditedData: (data) => set({
        editedData: data,
      }),

      updateEditedField: (field, value) => set((state) => ({
        editedData: { ...state.editedData, [field]: value },
      })),

      selectChantier: (ref, id) => set((state) => ({
        selectedChantiers: { ...state.selectedChantiers, [ref]: id },
      })),

      setStep: (step) => set({ step }),

      setContext: (chantierId, documentType, typeFrais) => set({
        chantierId,
        documentType,
        typeFrais: typeFrais || null,
      }),

      clearOCRData: () => set(initialState),

      hasData: () => {
        const state = get();
        return state.extractedData !== null && state.step !== 'upload';
      },

      // === Actions multi-scan ===
      setMultiScanSession: (session) => set({ multiScanSession: session }),

      updateMultiScanLine: (lineId, update) => set((state) => {
        if (!state.multiScanSession) return {};
        return {
          multiScanSession: {
            ...state.multiScanSession,
            resultLines: state.multiScanSession.resultLines.map((line) =>
              line.id === lineId ? { ...line, ...update } : line
            ),
          },
        };
      }),

      clearMultiScanSession: () => set({ multiScanSession: null }),
    }),
    {
      name: 'b8ild_ocr_pending',
      partialize: (state) => ({
        extractedData: state.extractedData,
        confidence: state.confidence,
        editedData: state.editedData,
        selectedChantiers: state.selectedChantiers,
        step: state.step,
        chantierId: state.chantierId,
        documentType: state.documentType,
        typeFrais: state.typeFrais,
        multiScanSession: state.multiScanSession,
      }),
      onRehydrateStorage: () => (state) => {
        // Expiration 24h des sessions multi-scan
        if (state?.multiScanSession) {
          const age = Date.now() - state.multiScanSession.createdAt;
          if (age > MULTI_SCAN_EXPIRATION_MS) {
            state.multiScanSession = null;
          }
        }
      },
    }
  )
);
